import { normalizeCorporateNumber } from "@/utils/corporateNumber";
import type { CorporateMasterSearchConditions } from "../components/corporateMasterSearchSchema";
import {
  COMMERCIAL_ACQUISITION_STATUSES,
  INDUSTRY_MAJOR_CODES,
  LISTING_FILTERS,
  type IndustryMajorCode,
} from "../constants";
import type {
  CommercialAcquisitionStatus,
  CorporateListParams,
  ListingFilter,
} from "../types";

export const defaultSearchConditions: CorporateMasterSearchConditions = {
  name: "",
  corporateNumber: "",
  industry: [],
  prefecture: [],
  city: [],
  kind: [],
  capitalMin: "",
  capitalMax: "",
  employeesMin: "",
  employeesMax: "",
  yearFrom: "",
  yearTo: "",
  foundingYearFrom: "",
  foundingYearTo: "",
  listing: "",
  commercialStatus: [],
  salesMin: "",
  salesMax: "",
  representative: "",
  registryOfficerName: "",
  includeFormerOfficers: false,
  registryTag: [],
  excludeAmbiguousRegistryTag: false,
};

type StringKey = {
  [K in keyof CorporateMasterSearchConditions]: CorporateMasterSearchConditions[K] extends string
    ? K
    : never;
}[keyof CorporateMasterSearchConditions];

const TEXT_KEYS = [
  "name",
  "corporateNumber",
  "capitalMin",
  "capitalMax",
  "employeesMin",
  "employeesMax",
  "yearFrom",
  "yearTo",
  "foundingYearFrom",
  "foundingYearTo",
  "salesMin",
  "salesMax",
  "representative",
  "registryOfficerName",
] as const satisfies ReadonlyArray<Exclude<StringKey, "listing">>;

const isIndustryMajorCode = (value: string): value is IndustryMajorCode =>
  INDUSTRY_MAJOR_CODES.some((code) => code === value);

const isListingFilter = (value: string | null): value is ListingFilter =>
  LISTING_FILTERS.some((filter) => filter === value);

const isCommercialAcquisitionStatus = (
  value: string
): value is CommercialAcquisitionStatus =>
  COMMERCIAL_ACQUISITION_STATUSES.some((status) => status === value);

/**
 * URLクエリから検索条件を復元する（ブラウザバックで条件を戻すため、検索状態の正本はURL）。
 * 配列条件は同名キーの繰り返し、未知の値は捨てる
 * @param sp URLクエリ
 * @returns 検索条件
 */
export const paramsToConditions = (
  sp: URLSearchParams
): CorporateMasterSearchConditions => {
  const next: CorporateMasterSearchConditions = { ...defaultSearchConditions };
  TEXT_KEYS.forEach((key) => {
    next[key] = sp.get(key) ?? "";
  });
  // getAll() の配列は実行環境（jsdom 等）によって別の Array として作られ、SWR のキーで中身ではなく
  // 参照で比べられて描画のたびに取り直しが起きるため、通常の配列に詰め替える
  next.prefecture = [...sp.getAll("prefecture")];
  next.city = [...sp.getAll("city")];
  next.kind = [...sp.getAll("kind")];
  next.industry = [...sp.getAll("industry")].filter(isIndustryMajorCode);
  const listing = sp.get("listing");
  next.listing = isListingFilter(listing) ? listing : "";
  next.commercialStatus = [...sp.getAll("commercialStatus")].filter(
    isCommercialAcquisitionStatus
  );
  // タグのコードは選択肢 API が正本なので、空の値を捨てるだけでコードでは絞らない（未知のコードは BE が 400 にする）
  // getAll の結果は写してから filter する（jsdom では別 realm の配列になり、SWR のキーが描画ごとに変わって取り直し続ける）
  next.registryTag = [...sp.getAll("registryTag")].filter(Boolean);
  next.excludeAmbiguousRegistryTag =
    sp.get("excludeAmbiguousRegistryTag") === "true";
  next.includeFormerOfficers = sp.get("includeFormerOfficers") === "true";
  return next;
};

/**
 * 検索条件をURLクエリにする（前後空白を除き、空の条件は載せない）
 * @param values 検索条件
 * @returns URLクエリ
 */
export const conditionsToParams = (
  values: CorporateMasterSearchConditions
): URLSearchParams => {
  const sp = new URLSearchParams();
  (Object.keys(values) as Array<keyof CorporateMasterSearchConditions>).forEach(
    (key) => {
      const value = values[key];
      if (typeof value === "boolean") {
        if (value) sp.set(key, "true");
      } else if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry) sp.append(key, entry);
        });
      } else if (value.trim()) {
        sp.set(key, value.trim());
      }
    }
  );
  return sp;
};

const toNumber = (value: string): number | undefined =>
  value.trim() ? Number(value) : undefined;

/**
 * 検索条件を一覧APIのクエリにする。市区町村は都道府県の選択時のみ送る
 * @param values 検索条件
 * @returns APIクエリ
 */
export const toApiParams = (
  values: CorporateMasterSearchConditions
): CorporateListParams => ({
  name: values.name.trim(),
  corporateNumber: normalizeCorporateNumber(values.corporateNumber),
  industry: values.industry,
  kind: values.kind,
  listing: values.listing === "" ? undefined : values.listing,
  commercialStatus: values.commercialStatus,
  representative: values.representative.trim(),
  registryOfficerName: values.registryOfficerName.trim(),
  // 退任した人を含める指定は代表者名・役員名にだけ効くため、どちらかを入れたときだけ送る
  includeFormerOfficers:
    (values.representative.trim() || values.registryOfficerName.trim()) &&
    values.includeFormerOfficers
      ? true
      : undefined,
  prefecture: values.prefecture,
  city: values.prefecture.length ? values.city : [],
  capitalMin: toNumber(values.capitalMin),
  capitalMax: toNumber(values.capitalMax),
  employeesMin: toNumber(values.employeesMin),
  employeesMax: toNumber(values.employeesMax),
  establishmentYearFrom: toNumber(values.yearFrom),
  establishmentYearTo: toNumber(values.yearTo),
  foundingYearFrom: toNumber(values.foundingYearFrom),
  foundingYearTo: toNumber(values.foundingYearTo),
  salesMin: toNumber(values.salesMin),
  salesMax: toNumber(values.salesMax),
  registryTag: values.registryTag,
  // BE はタグなしの除外指定を 400 にするため、タグを選んだときだけ送る
  excludeAmbiguousRegistryTag:
    values.registryTag.length && values.excludeAmbiguousRegistryTag
      ? true
      : undefined,
});

/** 有効な絞り込み条件があるか（ページ指定・単独の市区町村は除外）。 */
export const hasCorporateSearchCondition = (
  params: CorporateListParams
): boolean =>
  Object.entries(params).some(([key, value]) => {
    if (key === "limit" || key === "offset") return false;
    if (key === "city") return false;
    if (Array.isArray(value))
      return value.some(
        (entry: unknown) => typeof entry === "string" && entry.trim() !== ""
      );
    if (typeof value === "string") return value.trim() !== "";
    return typeof value === "number" && Number.isFinite(value);
  });
